import React, {
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useState,
} from "react";
import styles from "./FullScreenWrapper.module.scss";
import IconPDF from "@src/assets/icons/content_progress_pdf.svg?react";
import IconAudio from "@src/assets/icons/content_progress_audio.svg?react";
import IconSlide from "@src/assets/icons/content_progress_slide.svg?react";
import IconVideo from "@src/assets/icons/content_progress_video.svg?react";
import IconForm from "@src/assets/icons/content_progress_feedback.svg?react";
import { Content, ContentType, Lesson, SllLesson, SllModule } from "../../../types";
import ExitModal from "../../program-details/exit-modal/ExitModal";
import { useAnalytics, Events, useUser } from "@hooks";
import { usePresentationContext } from "@hooks/usePresentationContext";
import { useStudentLessonContent } from "@src/hooks/useStudentLessonContent";
import { resetGoogleTranslateWidget } from "@src/utils/helpers";
import TranslationBadge from "@src/components/translation-provider/TranslationBadge";
import IconMaterial from "@src/assets/icons/pdfIcon.png";
import { logger } from '../../../utils/logger';

type Props = {
  content: Content | SllModule;
  lesson?: Lesson | SllLesson;
  goBack?: () => void;
  children: (isFullScreen: boolean) => React.ReactNode;
  isMaterial?: boolean;
  isFullscreen?: boolean;
};

const getContentIcon = (type: ContentType) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    // case ContentType.GOOGLESLIDE:
    case ContentType.SLIDE: {
      return IconSlide;
    }
    case ContentType.REVEALSLIDE:
    case ContentType.ZIPSLIDE: {
      return IconSlide;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconForm;
    }
    default: {
      return null;
    }
  }
};

function FullScreenWrapper(props: Props) {
  const { children, content, lesson, isMaterial = false, goBack } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const topbar = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);
  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regexs.some((b) => userAgent.match(b));
  }, []);

  const { setLastContent, showExitTicket, setShowMaterial } = useStudentLessonContent();
  const [showGoogleTransWidget, setShowGoogleTransWidget] = useState(false);

  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  const { setIsPresenting } = usePresentationContext();
  
  // Set presentation state immediately when component mounts
  useEffect(() => {
    logger.info('StudentFullScreenWrapper mounted - setting presentation mode to TRUE');
    setIsPresenting(true);
    
    return () => {
      logger.info('StudentFullScreenWrapper unmounted - ending presentation');
      setIsPresenting(false);
    };
  }, [setIsPresenting]); // Include setIsPresenting in the dependency array
  
  // Check if user is from Australia
  const isAUuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("au");
  }, [user?.id]);

  // Translation is now handled by the TranslationBadge component

  const handleOpenMaterial = useCallback(() => {
    resetGoogleTranslateWidget();
    setShowMaterial(true)
  }, [setShowMaterial])

  const onSave = useCallback(async () => {
    setShowExitModal(false);
    if (goBack) {
      goBack();
    }

    trackEvent(Events.actions.exitLesson, {
      standalone_lesson: true,
      lesson_name: lesson?.name,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [goBack, lesson?.name, trackEvent, user]);

  const onNoSave = useCallback(async () => {
    setShowExitModal(false);
    if (goBack) {
      goBack();
    }

    trackEvent(Events.actions.exitLesson, {
      standalone_lesson: true,
      lesson_name: lesson?.name,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [goBack, lesson?.name, trackEvent, user]);

  const modules = useMemo(() => {
    // Main Lesson
    if (
      lesson && lesson.__typename === 'SLLLesson' &&
      lesson.presentation?.find((item) => item?.id === content?.id)
    ) {
      const preExitTicket = lesson.presentation.find(
        (module) =>
          module?.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.SLIDE
      );
      const exitTicket = lesson.presentation.find(
        (module) =>
          module?.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.FORM
      );
      if (preExitTicket && exitTicket) {
        return lesson.presentation.filter(
          (module) => module?.lastInLesson !== true && module !== preExitTicket
        ); // Remove "Lesson Complete" and "PreExitTicket" module on Progress
      }
      return lesson.presentation.filter(
        (module) => module?.lastInLesson !== true
      ); // Remove Lesson Complete module on Progress
    }
    // Normal Unit Lesson
    else if (
      lesson && lesson.__typename === 'Lesson' &&
      lesson.modules?.find((item) => item?.id === content.id)) {
      const preExitTicket = lesson.modules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.SLIDE
      );
      const exitTicket = lesson.modules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.FORM
      );
      if (preExitTicket && exitTicket) {
        return lesson.modules.filter(
          (module) => module.lastInLesson !== true && module !== preExitTicket
        ); // Remove "Lesson Complete" and "PreExitTicket" module on Progress
      }
      return lesson.modules.filter(module => module.data).filter((module) => module.lastInLesson !== true); // Remove Lesson Complete module on Progress
    }
    else {
      return [];
    }
  }, [content, lesson]);
  // check which one is class presentation
  const moduleIndex = useMemo(() => {
    // Standalone Lesson
    if (
      lesson && lesson.__typename === 'SLLLesson' &&
      lesson.presentation?.find((item) => item?.id === content?.id)
    ) {
      const progressModules = lesson.presentation.filter(
        (module) => module?.lastInLesson !== true
      );
      const lessonCompleteModule = lesson.presentation.find(
        (module) => module?.lastInLesson === true
      );
      if (lessonCompleteModule && lessonCompleteModule.id === content?.id) {
        return lesson.presentation.findIndex(
          (module) => module === lessonCompleteModule
        );
      }
      return progressModules.findIndex((m) => m?.id === content?.id);
    }
    // Normal Unit Lesson
    else if (
      lesson && lesson.__typename === 'Lesson' &&
      lesson.modules?.find((item) => item?.id === content.id)) {
      const progressModules = lesson.modules.filter(module => module.data).filter(
        (module) => module.lastInLesson !== true
      );
      const lessonCompleteModule = lesson.modules.find(
        (module) => module.lastInLesson === true
      );
      if (lessonCompleteModule && lessonCompleteModule.id === content.id) {
        return lesson.modules.findIndex(
          (module) => module === lessonCompleteModule
        );
      }
      return progressModules.findIndex((m) => m?.id === content?.id);
    }
    return -1;
  }, [content, lesson]);

  const survey = useMemo(() => {
    // Exit Ticket
    if (content?.type === ContentType.FORM && showExitTicket) {
      return true;
    } else {
      return false;
    }
  }, [content, showExitTicket]);

  useEffect(() => {
    // Standalone Lesson
    if (
      lesson && lesson.__typename === 'SLLLesson' &&
      lesson.presentation?.find((item) => item?.id === content?.id) &&
      progressRef.current
    ) {
      var lessonpresentation = lesson.presentation.filter(
        (module) => module?.lastInLesson !== true
      );
      const preExitTicket = lessonpresentation.find(
        (module) =>
          module?.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.SLIDE
      );
      const exitTicket = lessonpresentation.find(
        (module) =>
          module?.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.FORM
      );
      const lessonCompleteModule = lesson.presentation.find(
        (module) => module?.lastInLesson === true
      );

      var presentationIndex = modules.findIndex((m) => m?.id === content?.id);
      if (preExitTicket && exitTicket && preExitTicket.id === content?.id) {
        presentationIndex = modules.findIndex((m) => m?.id === exitTicket.id);
      } else if (
        lessonCompleteModule &&
        lessonCompleteModule.id === content?.id
      ) {
        presentationIndex = lesson.presentation.findIndex(
          (m) => m?.id === lessonCompleteModule.id
        );
      }
      if (presentationIndex > 0) {
        if (presentationIndex < modules.length - 1) {
          setProgressWidth(
            ((progressRef.current.clientWidth - 50) * presentationIndex) /
              (modules.length - 1)
          );
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0);
          setLastContent(false);
        }
      }
    } 
    // Normal Unit Lesson
    else if (
      lesson && lesson.__typename === 'Lesson' &&
      lesson.modules?.find((item) => item?.id === content.id) &&
      progressRef.current
    ) {
      var lessonmodules = lesson.modules.filter(module => module.data).filter(
        (module) => module.lastInLesson !== true
      );
      const preExitTicket = lessonmodules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.SLIDE
      );
      const exitTicket = lessonmodules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.FORM
      );
      const lessonCompleteModule = lesson.modules.find(
        (module) => module.lastInLesson === true
      );

      var moduleIndex = modules.findIndex((m) => m?.id === content?.id);
      if (preExitTicket && exitTicket && preExitTicket.id === content.id) {
        moduleIndex = modules.findIndex((m) => m?.id === exitTicket.id);
      } else if (
        lessonCompleteModule &&
        lessonCompleteModule.id === content.id
      ) {
        moduleIndex = lesson.modules.findIndex(
          (m) => m.id === lessonCompleteModule.id
        );
      }
      if (moduleIndex > 0) {
        if (moduleIndex < modules.length - 1) {
          setProgressWidth(
            ((progressRef.current.clientWidth - 50) * moduleIndex) /
              (modules.length - 1)
          );
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0);
          setLastContent(false);
        }
      }
    }
  }, [content, lesson, modules, setLastContent]);

  const showMaterailModal = useMemo(() => {
    const materialLesson = [...lesson?.materials ?? []]?.find(material => (material?.name.toLowerCase().includes('student')))
    return materialLesson ? true : false
  }, [lesson?.materials])

  const fullScreenButtonBlock = useMemo(() => {
    const percent =
      modules.length === 1 ? 100 : ((moduleIndex + 1) / modules.length) * 100;
    const isClassPresentation = moduleIndex > -1;

    return (
      <div
        ref={topbar}
        className={`${styles.fullScreenActions} ${styles.noPadding}`}
        style={{
          backgroundColor: survey ? "white" : "black",
        }}
      >
        {isClassPresentation && (
          <div ref={progressRef} className={styles.progressBackBar}>
            <div
              className={styles.progressBar}
              style={{
                width: `${progressWidth}px`,
                borderRadius: percent === 100 ? "3px" : "",
              }}
            ></div>
            <div className={styles.iconView}>
              {modules.map((item: any) => {
                if (item.type === ContentType.PARENT) {
                  return <div key={item.id}>PS</div>;
                } else {
                  const Icon = getContentIcon(item.type);
                  return (
                    <div key={item.id}>
                      {Icon && <Icon className={styles.moduleIcon} />}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}
        {showMaterailModal && <div className={styles.translateRectButton} onClick={handleOpenMaterial}>
          <div className={styles.translateIconWrapper}>
            <img className={styles.pdfIcon} src={IconMaterial} alt="Icon" />
          </div>
          <span className={styles.translateText}>Lesson Materials</span>
        </div>}
        {/* Translation Badge for full screen mode - hidden for Australian users */}
        {!isAUuser && (
          <TranslationBadge type="presentation" />
        )}
      </div>
    );
  }, [modules, moduleIndex, survey, progressWidth, showMaterailModal, handleOpenMaterial, isAUuser]);

  return (
    <div
      id="fswrapper"
      className={styles.wrapper}
      ref={wrapperRef}
      style={{
        backgroundColor:
          survey || isMaterial ? "white" : isPhone ? "#535353" : "black",
      }}
    >
      {!isPhone && !isMaterial ? fullScreenButtonBlock : null}
      <div
        className={`${styles.content} ${styles.isFull}`}
        style={{ marginTop: isMaterial ? 0 : 75 }}
      >
        {children(true)}
      </div>
      {showExitModal && (
        <ExitModal
          open={showExitModal}
          onSave={onSave}
          onNoSave={onNoSave}
          onClose={() => setShowExitModal(false)}
          loading={false}
        />
      )}
    </div>
  );
}

export default FullScreenWrapper;
